/**
 * QUOTATION FORM CONTEXT — come un plugin partecipa al salvataggio del preventivo.
 *
 * ## Il problema che risolve
 *
 * Nel dettaglio ogni campo si salva da solo: il preventivo esiste, il plugin ne conosce l'id e
 * chiama il proprio endpoint quando vuole. **In creazione no.** Il preventivo non esiste ancora,
 * quindi il plugin non ha un `quotationId` a cui agganciare i propri campi, e non può salvare
 * niente finché il core non ha finito.
 *
 * Serve quindi un'inversione: il plugin non salva, si **dichiara disponibile** a salvare; il
 * core crea il preventivo, ottiene l'id e invoca chi si era dichiarato.
 *
 * ```tsx
 * // in un componente montato nello slot 'form:extra'
 * const { registerExtension } = useQuotationFormContext();
 *
 * useEffect(() => registerExtension({
 *   key: 'trentino',
 *   submit: async (quotationId) => { await tsApi.saveDetail(quotationId, draft); },
 * }), [registerExtension, draft]);
 * ```
 *
 * `registerExtension` restituisce la funzione di disiscrizione, così l'effect la usa come
 * cleanup e la registrazione segue il ciclo di vita del componente senza che il plugin debba
 * ricordarsi di annullarla.
 *
 * ## Perché una mappa per chiave e non una lista
 *
 * La `submit` cattura lo stato del plugin, quindi cambia a ogni battuta sulla tastiera. Con una
 * lista, ogni ri-registrazione aggiungerebbe una voce e il salvataggio verrebbe eseguito decine
 * di volte. Con la chiave, la ri-registrazione **sostituisce**: alla fine ne resta una per
 * plugin, l'ultima, che è quella che conosce lo stato aggiornato.
 *
 * ## Cosa il core NON garantisce
 *
 * Le chiamate REST del core e dei plugin sono transazioni distinte. Se il core riesce e un
 * plugin fallisce, **il preventivo esiste senza i campi di quel plugin**: non c'è rollback, e
 * fingere che ci sia sarebbe peggio che dirlo. Il core lo comunica esplicitamente e offre di
 * ritentare i soli plugin falliti — vedi `QuotationForm`.
 */

import { createContext, useContext } from 'react';

export interface QuotationFormExtension {
  /** Identifica il plugin. Ri-registrare la stessa chiave sostituisce la registrazione. */
  key: string;
  /**
   * Salva i campi di estensione sul preventivo appena creato o aggiornato.
   *
   * Deve rifiutare in caso di errore: è dal reject che il core capisce di dover segnalare il
   * salvataggio parziale. Una `submit` che ingoia i propri errori fa credere all'utente che sia
   * andato tutto bene.
   */
  submit: (quotationId: number) => Promise<void>;
}

export interface QuotationFormContextValue {
  /** Registra l'estensione e restituisce la funzione di disiscrizione. */
  registerExtension: (extension: QuotationFormExtension) => () => void;
}

export const QuotationFormContext = createContext<QuotationFormContextValue | null>(null);

/**
 * Il contesto del form del preventivo.
 *
 * Solleva se chiamato fuori dal form invece di restituire un no-op: una registrazione che non
 * arriva a nessuno è un salvataggio che non avviene, e in silenzio. Meglio che il plugin se ne
 * accorga al primo render.
 */
export function useQuotationFormContext(): QuotationFormContextValue {
  const context = useContext(QuotationFormContext);

  if (!context)
    throw new Error(
      'useQuotationFormContext va usato dentro QuotationForm, tipicamente da un componente ' +
      'contribuito nello slot form:extra.',
    );

  return context;
}
